import {Injectable} from '@angular/core';
import {CompanyType, UserGroup} from '@app/model/user/dcp-user';
import {ConfigService, DcpGlobalMessageService} from '@dcp/configurator';
import {AuthService, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {OAuthService} from 'angular-oauth2-oidc';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {DcpCustomerRegistrationDecoratorService} from '../dcp-customer-registration-decorator.service';

@Injectable({
  providedIn: 'root',
})
export class DcpUserService {

  private autoBpCreationEligibleUsers: string[] = this.configService.getStringArray('registration.autoBpCreationEligibleUsers', []);

  constructor(
    protected authService: AuthService,
    protected userFacade: UserAccountFacade,
    private registrationService: DcpCustomerRegistrationDecoratorService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private oAuthService: OAuthService,
    private configService: ConfigService
  ) {
    this.userFacade.get();
  }

  /**
   * Check if the user email ends with regExp
   *
   * @param email - email address by which the check if user is SAP employee
   * @param domains - list of allowed domains
   */
  public userIsEmployee(email: string, domains: any): boolean {
    return domains ? domains.some((domain) => email?.includes(domain)) : /@sap.com$/.test(email);
  }

  /**
   * Check if the currently logged-in user has the EMPLOYEE value as group claim
   */
  public hasEmployeeRole() {
    const claims: Record<string, any> = this.oAuthService.getIdentityClaims();
    const groups: Array<string> = claims?.groups;

    return groups?.includes(UserGroup.EMPLOYEE);
  }

  isUserCompanyTypeIndividual(user: User): boolean {
    return user?.companyType === CompanyType.INDIVIDUAL;
  }

  getUserCompanyType(user: User) {
    return this.isUserCompanyTypeIndividual(user) ? CompanyType.INDIVIDUAL : CompanyType.ORGANIZATION;
  }

  /**
   * As IDS might not have populated the sapContactID to our cloud commerce system,
   * we use registrationService.getCustomer to check if it is present.
   *
   * @param user User currently logged in
   */
  isCustomerKnown(user: User): Observable<boolean> {
    if (user?.companyType?.length) {
      return of(true);
    } else if (user?.scimUserId) {
      return this.registrationService.getCustomer(user.scimUserId).pipe(
        map((customer) => !!customer),
        switchMap((isKnown) =>
          // assign 'organization' to b2b unit and return 'isKnown'
          isKnown ? this.assignOrganizationToB2bUnit().pipe(map(() => true)) : of(false)
        ),
        catchError((e) => {
          this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
          return of(false);
        })
      );
    }
    return of(false);
  }

  assignOrganizationToB2bUnit(): Observable<any> {
    return this.registrationService
      .setCompanyType(CompanyType.ORGANIZATION)
      .pipe(catchError(async (e) => this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging')));
  }

  /**
   * If the user's uid contains dca, it is still not replicated to SAP.
   *
   * @param user User currently logged in
   */
  isCustomerReplicatedInCrm(user: User): boolean {
    return !!user && !user?.uid?.startsWith('dca');
  }

  /**
   * Checks if the user's SCIM id is eligible for auto-bp creation
   *
   * @param user User currently logged in
   */
  isAutoBpCreationEligibleUser(user: User): boolean {
    return this.autoBpCreationEligibleUsers.some(prefix => user?.scimUserId?.includes(prefix));
  }
}
